import { compose } from "redux";
import classNames from "classnames";
import { connect } from "react-redux";
import React, { PureComponent } from "react";
import { amber } from "@material-ui/core/colors";
import { withStyles } from "@material-ui/core/styles";
import { Chip, Grid, Paper, Typography } from "@material-ui/core";

import { withToggleModal } from "altus-ui-components";

import {
  GROUP_TYPES,
  CARD_CONTAINER_TYPE,
  EDIT_LOCATION_GROUP_MODAL_ID,
} from "features/planner/planner.constants";

import {
  getGroupFromState,
  getCardCountByGroupFromState,
  createCardCountByCardContainerTypeSelector,
  getCardCountByCardContainerTypeFromState,
} from "features/planner/planner.selectors";

import { GroupMapper } from "features/planner/planner.mappers";
import { initializeEditLocationGroup } from "features/planner/planner.actions";
import GroupHeaderMenu from "features/planner/components/Group/GroupHeaderMenu";
import GroupFlagTypeBtn from "features/planner/components/Group/GroupFlagTypeBtn";

class GroupHeader extends PureComponent {
  toggleEditLocationGroup = () => {
    const {
      group,
      dispatchToggleModal,
      dispatchInitializeEditLocationGroup,
    } = this.props;

    dispatchInitializeEditLocationGroup(group, () =>
      dispatchToggleModal({ modalId: EDIT_LOCATION_GROUP_MODAL_ID }),
    );
  };

  getOffshoreOnshoreCountLabel = () => {
    const {
      group,
      onshoreCardCount,
      offshoreCardCount,
      allocatedCardCount,
    } = this.props;

    switch (group.get("type")) {
      case GROUP_TYPES.LOCATION_GROUP:
        return `${allocatedCardCount} | ${offshoreCardCount}`;
      case GROUP_TYPES.PLANNING_GROUP:
        return onshoreCardCount;
      default:
        return undefined;
    }
  };

  getOffshoreOnshoreCountTitle = () => {
    const {
      group,
      onshoreCardCount,
      offshoreCardCount,
      allocatedCardCount,
    } = this.props;

    switch (group.get("type")) {
      case GROUP_TYPES.LOCATION_GROUP:
        return `Allocated: ${allocatedCardCount} | Offshore: ${offshoreCardCount}`;
      case GROUP_TYPES.PLANNING_GROUP:
        return `Onshore: ${onshoreCardCount}`;
      default:
        return undefined;
    }
  };

  render() {
    const {
      group,
      viewId,
      groupId,
      classes,
      expanded,
      draggable,
      isDragging,
      viewGroupId,
      displayMenu,
      totalCardCount,
      displayExpandBtn,
      displayDeleteBtn,
      disableFlagTypeBtn,
    } = this.props;

    const isLocationGroup = group.get("type") === GROUP_TYPES.LOCATION_GROUP;

    const groupIsHighlighted = isLocationGroup && !!totalCardCount && !expanded;

    return (
      <Paper
        square
        elevation={0}
        className={classNames(classes.root, {
          [classes.rootDragging]: isDragging,
          [classes.rootDraggable]: draggable,
        })}
      >
        <Grid container alignItems="center" wrap="nowrap">
          <GroupFlagTypeBtn group={group} disabled={disableFlagTypeBtn} />
          <Grid item zeroMinWidth>
            <Typography
              noWrap
              variant="body2"
              color="secondary"
              title={isLocationGroup ? "Edit Location Group" : ""}
              onClick={isLocationGroup ? this.toggleEditLocationGroup : null}
              className={classNames(classes.viewGroupHeaderContent, {
                [classes.viewGroupHeaderContentHover]: isLocationGroup,
              })}
            >
              {group.get("name")}
            </Typography>
          </Grid>
          <Grid
            xs
            item
            container
            spacing={1}
            wrap="nowrap"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Grid item>
              <Chip
                title={this.getOffshoreOnshoreCountTitle()}
                label={this.getOffshoreOnshoreCountLabel()}
                classes={{
                  root: classNames(classes.chipRoot, {
                    // display highlight color if there are any cards in a collapsed group
                    [classes.chipRootHighlighted]: groupIsHighlighted,
                  }),
                }}
              />
            </Grid>
            <GroupHeaderMenu
              group={group}
              viewId={viewId}
              groupId={groupId}
              expanded={expanded}
              displayMenu={displayMenu}
              viewGroupId={viewGroupId}
              groupType={group.get("type")}
              displayDeleteBtn={displayDeleteBtn}
              displayExpandBtn={displayExpandBtn}
              initializeEditLocationGroup={this.toggleEditLocationGroup}
            />
          </Grid>
        </Grid>
      </Paper>
    );
  }
}

const styles = theme => ({
  root: {
    top: 0,
    zIndex: 2,
    width: "100%",
    display: "flex",
    position: "sticky",
    alignItems: "center",
    paddingRight: theme.spacing(1),
    background: theme.altus.background.headerDark,
  },
  rootDragging: {
    opacity: 0.2,
  },
  rootDraggable: {
    cursor: "move",
  },
  viewGroupHeaderContent: {
    fontWeight: 700,
    padding: theme.spacing(1),
  },
  viewGroupHeaderContentHover: {
    "&:hover": {
      cursor: "pointer",
      textDecoration: "underline",
    },
  },
  chipRoot: {
    height: "1.5rem",
    background: theme.altus.background.headerChip,
    color: theme.palette.primary.contrastText,
    fontWeight: theme.typography.fontWeightBold,
  },
  chipRootHighlighted: {
    background: amber["A200"],
    color: theme.palette.text.primary,
  },
});

GroupHeader.defaultProps = {
  group: GroupMapper.initial(),
};

export default compose(
  connect(
    (_, { groupId }) => {
      const cardCountSelector = getCardCountByGroupFromState(groupId);

      // create one selector that is reused for the selectors below, for performance reasons
      const cardCountByCardContainerSelector = getCardCountByCardContainerTypeFromState(
        groupId,
      );

      const onshoreCardCountSelector = createCardCountByCardContainerTypeSelector(
        cardCountByCardContainerSelector,
        CARD_CONTAINER_TYPE.ONSHORE,
      );

      const allocatedCardCountSelector = createCardCountByCardContainerTypeSelector(
        cardCountByCardContainerSelector,
        CARD_CONTAINER_TYPE.ALLOCATED_OFFSHORE,
      );

      const offshoreCardCountSelector = createCardCountByCardContainerTypeSelector(
        cardCountByCardContainerSelector,
        CARD_CONTAINER_TYPE.DEPLOYED_OFFSHORE,
      );

      return state => ({
        totalCardCount: cardCountSelector(state),
        group: getGroupFromState(state, groupId),
        onshoreCardCount: onshoreCardCountSelector(state),
        offshoreCardCount: offshoreCardCountSelector(state),
        allocatedCardCount: allocatedCardCountSelector(state),
      });
    },
    {
      dispatchInitializeEditLocationGroup: initializeEditLocationGroup,
    },
  ),
  withToggleModal,
  withStyles(styles),
)(GroupHeader);
